import type { DeviceInfo, UITreeResponse } from "../types";
import { getServerBase } from "./settings";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    message: string,
    /** Machine-readable error code from the server's JSON body, if any. */
    public readonly code?: string,
  ) {
    super(message);
  }
}

function base(): string {
  return getServerBase();
}

export function apiUrl(path: string): string {
  return `${base()}${path.startsWith("/") ? path : "/" + path}`;
}

// Per-tab stream client ID. Sent with every MJPEG request so the server
// can supersede a previous stream from the same tab (e.g. when fps
// changes) — WebKit leaves the old multipart response open on src swap,
// so we rely on the server to close it once the new one is live.
const streamClientId: string =
  (typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Mint a per-stream token. Used both as a cache-buster (WebKit can
// otherwise collapse identical URLs) and as the server-side stream
// instance ID for DELETE — so an explicit stop can't race a supersede.
export function newStreamToken(): string {
  return String(Date.now()) + "-" + Math.random().toString(36).slice(2, 8);
}

export function streamUrl(deviceId: string, fps: number, token: string): string {
  return `${base()}/device/${encodeURIComponent(deviceId)}/stream?fps=${fps}&client=${encodeURIComponent(streamClientId)}&t=${encodeURIComponent(token)}`;
}

// WebSocket URL for the quota-exempt manual-actions channel. The web
// UI sends tap/drag/go-home through this and receives a debounced
// ui-tree push after each action.
export function actionsWSUrl(deviceId: string): string {
  const httpBase = base();
  const wsBase = httpBase.replace(/^http/i, "ws");
  return `${wsBase}/device/${encodeURIComponent(deviceId)}/actions`;
}

// Stop the stream previously opened with the same token. WebKit does
// not abort an in-flight multipart <img> response on unmount or src
// change, so we ask the server to cancel it server-side.
export function stopStream(deviceId: string, token: string): void {
  const url = `${base()}/device/${encodeURIComponent(deviceId)}/stream?client=${encodeURIComponent(streamClientId)}&t=${encodeURIComponent(token)}`;
  try {
    fetch(url, { method: "DELETE", keepalive: true }).catch(() => {});
  } catch {
    /* no-op */
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = apiUrl(path);
  const resp = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(init?.headers || {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    // Server sends {"error": "...", "code": "..."} for structured errors.
    // Pull the code out so callers can branch on it without string-matching.
    let message = text || resp.statusText;
    let code: string | undefined;
    if (text) {
      try {
        const body = JSON.parse(text);
        if (body && typeof body === "object") {
          if (typeof body.error === "string") message = body.error;
          if (typeof body.code === "string") code = body.code;
        }
      } catch {
        /* non-JSON body — use raw text as message */
      }
    }
    throw new ApiError(resp.status, path, message, code);
  }
  if (resp.status === 204) return undefined as T;
  const ct = resp.headers.get("content-type") || "";
  if (ct.includes("application/json")) return (await resp.json()) as T;
  return (await resp.text()) as unknown as T;
}

export async function listDevices(): Promise<DeviceInfo[]> {
  return request<DeviceInfo[]>("/api/v1/devices");
}

export async function getDevice(id: string): Promise<DeviceInfo> {
  return request<DeviceInfo>(`/api/v1/devices/${encodeURIComponent(id)}`);
}

export interface SimulatorInfo {
  udid: string;
  name: string;
  state: "Booted" | "Shutdown" | string;
  runtime: string;
  osVersion: string;
}

export async function listSimulators(): Promise<SimulatorInfo[]> {
  return request<SimulatorInfo[]>("/api/v1/simulators");
}

export async function bootSimulator(udid: string): Promise<void> {
  await request<void>(
    `/api/v1/simulators/${encodeURIComponent(udid)}/boot`,
    { method: "POST" },
  );
}

export async function shutdownSimulator(udid: string): Promise<void> {
  await request<void>(
    `/api/v1/simulators/${encodeURIComponent(udid)}/shutdown`,
    { method: "POST" },
  );
}

export async function health(): Promise<{ status: string }> {
  return request<{ status: string }>("/api/v1/health");
}

/**
 * Minimum MobAI desktop app version this web client talks to. Bumped
 * whenever we rely on a new server endpoint or payload shape.
 */
export const MIN_MOBAI_VERSION = "1.9.0";

/**
 * Fetch the running MobAI app version. Returns null when the endpoint
 * 404s — that means the installed app predates `/api/v1/version` and is
 * therefore outdated by definition.
 */
export async function getServerVersion(): Promise<string | null> {
  try {
    const resp = await fetch(apiUrl("/api/v1/version"), {
      headers: { Accept: "application/json" },
    });
    if (resp.status === 404) return null;
    if (!resp.ok)
      throw new ApiError(resp.status, "/api/v1/version", resp.statusText);
    const body = (await resp.json()) as { version?: string };
    return body.version ?? null;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(0, "/api/v1/version", String(e));
  }
}

/**
 * Semver-ish comparator. Returns negative if a < b, 0 if equal, positive
 * if a > b. Only looks at dotted integer segments; trailing suffixes
 * (`-rc.1`) are ignored.
 */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) =>
    v
      .replace(/^v/, "")
      .split("-")[0]
      .split(".")
      .map((n) => parseInt(n, 10) || 0);
  const as = parse(a);
  const bs = parse(b);
  const len = Math.max(as.length, bs.length);
  for (let i = 0; i < len; i++) {
    const diff = (as[i] ?? 0) - (bs[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export async function pingHealthAt(
  serverBase: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const url = `${serverBase.replace(/\/+$/, "")}/api/v1/health`;
  try {
    const resp = await fetch(url, {
      signal,
      headers: { Accept: "application/json" },
    });
    return resp.ok;
  } catch {
    return false;
  }
}

export async function getUITree(deviceId: string): Promise<UITreeResponse> {
  return request<UITreeResponse>(
    `/api/v1/devices/${encodeURIComponent(deviceId)}/ui-tree?verbose=true`,
  );
}

// Device actions — all return the server's { success, data? } wrapper.
export async function postAction<T = unknown>(
  deviceId: string,
  action: string,
  body: unknown,
): Promise<T> {
  return request<T>(
    `/api/v1/devices/${encodeURIComponent(deviceId)}/${action}`,
    {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    },
  );
}

// Explicit bridge-start handshake. Idempotent: returns 200 if already running.
// On a free-tier second-device attempt the server returns 402 with
// `code: "device_limit_reached"` — callers use that to stop opening the
// action WS / MJPEG stream, which would otherwise retry in a loop.
export async function startBridge(deviceId: string): Promise<void> {
  await request<void>(
    `/api/v1/devices/${encodeURIComponent(deviceId)}/bridge/start`,
    { method: "POST" },
  );
}
